import React, { useState, useEffect, useMemo, Suspense } from "react";
import axiosInstance from "../api/axiosInstance";
import {
    ColumnDef,
    useReactTable,
    getCoreRowModel,
    PaginationState,
    flexRender
} from "@tanstack/react-table";
import { showGlobalToast } from "../utils/toast";
import { OrderFilters, FiltersState } from "./OrderFilters";
import { StatusBadge, PaymentBadge } from "../utils/badges";

const OrderDetailsModal = React.lazy(() => import("./OrderDetailsModel"));

export interface OrderItem {
    orderItemId: number;
    orderId: number;
    menuItemId: number;
    menuItemName: string;
    quantity: number;
    price: number;
    discountedPrice: number | null;
    cookingInstructions: string;
    status: string;
}

export interface Order {
    orderId: number;
    linkedOrders: string[];
    tableNumber: string;
    tableLabel: string;
    placedByName: string;
    placedByContactDetails: string;
    orderStatus: "Accepted" | "Completed" | "Cancelled" | string;
    isPaid: boolean;
    paymentId: number | null;
    invoiceNo: string;
    placedAt: string;
    totalAmount: number;
    finalAmount: number;
    items: OrderItem[];
}

const formatDateTime = (isoString: string) =>
    new Date(isoString).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
    });

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
    }).format(amount || 0);

const OrderHistoryForRestaurant: React.FC = () => {
    const [data, setData] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [pageCount, setPageCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    const [pendingFilters, setPendingFilters] = useState<FiltersState>({});
    const [appliedFilters, setAppliedFilters] = useState<FiltersState>({});

    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [invoiceCache, setInvoiceCache] = useState<Record<string, string>>({});
    const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
    const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
    const payload = useMemo(
        () => ({
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            filters: appliedFilters,
        }),
        [pagination.pageIndex, pagination.pageSize, appliedFilters]
    );

    // ===================== API Fetch =====================
    useEffect(() => {
        let cancelled = false;
        const fetchOrders = async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.post("/Order/OrderHistoryForRestaurant", payload);
                if (cancelled) return;

                const rows: Order[] = res?.data?.data ?? [];
                setData(rows);
                const totalPages =
                    res?.data?.totalPages ??
                    (res?.data?.totalCount && payload.pageSize
                        ? Math.ceil(res.data.totalCount / payload.pageSize)
                        : 0);
                setPageCount(totalPages);
                setTotalCount(res?.data?.totalCount ?? 0);
            } catch (err) {
                if (!cancelled) {
                    console.error("Error fetching orders:", err);
                    setData([]);
                    setPageCount(0);
                    setTotalCount(0);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchOrders();
        return () => {
            cancelled = true;
        };
    }, [payload]);

    // ===================== Table Columns =====================
    const columns: ColumnDef<Order>[] = useMemo(
        () => [
            {
                header: "Order ID",
                accessorKey: "orderId",
                cell: (info) => (
                    <span className="font-medium text-orange-600 dark:text-orange-400">
                        #{info.getValue<number>()}
                    </span>
                ),
            },
            {
                header: "Date & Time",
                accessorKey: "placedAt",
                cell: (info) => formatDateTime(info.getValue<string>()),
            },
            {
                header: "Table",
                accessorFn: (row) => row.tableLabel || row.tableNumber,
            },
            {
                header: "Amount",
                accessorKey: "finalAmount",
                cell: (info) => formatCurrency(info.getValue<number>()),
            },
            {
                header: "Status",
                accessorKey: "orderStatus",
                cell: (info) => <StatusBadge status={info.getValue<string>()} />,
            },
            {
                header: "Payment",
                accessorKey: "isPaid",
                cell: (info) => <PaymentBadge isPaid={info.getValue<boolean>()} />,
            },
            {
                header: "Actions",
                cell: (info) => (
                    <button
                        onClick={() => setSelectedOrder(info.row.original)}
                        className="font-medium text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300"
                    >
                        View Details
                    </button>
                ),
            },
        ],
        []
    );

    const table = useReactTable({
        data,
        columns,
        pageCount,
        state: { pagination },
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        onPaginationChange: setPagination,
    });

    // ===================== Invoice Fetch =====================
    const fetchInvoiceHtml = async (
        id: string,
        invoiceNo: string,
        groupedBy: string,
        paymentId?: number
    ) => {
        try {
            setIsLoadingInvoice(true);
            if (paymentId && paymentId > 0 && invoiceCache[paymentId]) {
                // Already cached, no API call needed
                setInvoicePreviewOpen(true);
                return invoiceCache[paymentId];
            }
            const res = await axiosInstance.get(`/billing/GetBillHtml/${groupedBy}/${id}/${invoiceNo}`, {
                params: { includeServiceCharge: true, paymentId: paymentId ?? 0 },
            });
            //setInvoiceHtml(res.data.htmlContent ?? null);
            setInvoiceCache(prev => ({ ...prev, [paymentId ?? 0]: res.data.htmlContent ?? '<h3> Not found </h3>' }));
            setInvoicePreviewOpen(true);
        } catch (err) {
            console.error("Error fetching invoice HTML:", err);
            showGlobalToast("Error fetching invoice HTML", "error");
            setInvoicePreviewOpen(false);
        } finally {
            setIsLoadingInvoice(false);
        }
    };

    // ===================== Filter Handlers =====================
    const applyFilters = () => {
        setAppliedFilters(pendingFilters);
        setPagination((old) => ({ ...old, pageIndex: 0 }));
    };

    const resetFilters = () => {
        setPendingFilters({});
        setAppliedFilters({});
        setPagination((old) => ({ ...old, pageIndex: 0 }));
    };

    return (
        <div className="w-full px-2 sm:px-4 md:px-6 lg:px-0 py-4 bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100 min-h-full">
            <div className="max-w-7xl mx-auto">
                <header className="mb-3 sm:mb-4">
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Order History</h1>
                    <p className="mt-1 text-[12px] sm:text-sm text-gray-500 dark:text-gray-400">
                        View and manage all past orders for your restaurant.
                    </p>
                </header>

                {/* Filters Panel */}
                <OrderFilters
                    pendingFilters={pendingFilters}
                    setPendingFilters={setPendingFilters}
                    applyFilters={applyFilters}
                    resetFilters={resetFilters}
                />

                {/* Orders Table */}
                <div className="overflow-hidden bg-white dark:bg-gray-800 rounded-lg shadow max-h-[70vh] flex flex-col">
                    <div className="flex-1 overflow-auto">
                        <table className="min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <th
                                                key={header.id}
                                                className="px-2 sm:px-3 py-2 text-left text-[11px] sm:text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap"
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </th>
                                        ))}
                                    </tr>
                                ))}

                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="text-center py-6 text-gray-500 text-sm">
                                            Loading...
                                        </td>
                                    </tr>
                                ) : table.getRowModel().rows.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                            {row.getVisibleCells().map((cell) => (
                                                <td
                                                    key={cell.id}
                                                    className="px-2 sm:px-3 py-2 text-[12px] sm:text-sm text-gray-900 dark:text-gray-100 align-top truncate"
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}

                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={columns.length} className="text-center py-6 text-gray-500 text-sm">
                                            No orders found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="sticky bottom-0 z-10 flex items-center justify-between p-2 sm:p-3 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur">
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] sm:text-sm">Rows per page:</span>
                            <select
                                value={pagination.pageSize}
                                onChange={(e) =>
                                    setPagination((old) => ({
                                        ...old,
                                        pageSize: Number(e.target.value),
                                        pageIndex: 0,
                                    }))
                                }
                                className="border rounded px-2 py-1 text-[11px] sm:text-sm dark:bg-gray-700 dark:text-white"
                            >
                                {[10, 20, 30, 50].map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-4 text-[11px] sm:text-sm">
                            <span className="text-gray-500 dark:text-gray-400">
                                Total Orders: <b>{totalCount}</b>
                            </span>
                            <button
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                                className="px-2 py-1 border rounded disabled:opacity-50"
                            >
                                Prev
                            </button>
                            <span>
                                Page {pagination.pageIndex + 1} of {pageCount || 1}
                            </span>
                            <button
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                                className="px-2 py-1 border rounded disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Order Details Modal */}
            <Suspense fallback={<div>Loading...</div>}>
                {selectedOrder && (
                    <OrderDetailsModal
                        order={selectedOrder}
                        customerInfo={{
                            name: selectedOrder.placedByName,
                            contact: selectedOrder.placedByContactDetails,
                        }}
                        onClose={() => {
                            // Clear the entire invoice cache
                            setInvoiceCache({});
                            setSelectedOrder(null);
                        }} fetchInvoiceHtml={fetchInvoiceHtml}
                        invoiceHtml={invoiceCache[selectedOrder?.paymentId ?? 0]}
                        invoicePreviewOpen={invoicePreviewOpen}
                        setInvoicePreviewOpen={setInvoicePreviewOpen}
                        isLoadingInvoice={isLoadingInvoice}
                    />
                )}
            </Suspense>
        </div>
    );
};

export default OrderHistoryForRestaurant;
